import { ActivityLog } from '../types';

export class ActivityLogger {
  private static instance: ActivityLogger;
  private logs: ActivityLog[] = [];
  private maxLogs = 1000;

  static getInstance(): ActivityLogger {
    if (!ActivityLogger.instance) {
      ActivityLogger.instance = new ActivityLogger();
    }
    return ActivityLogger.instance;
  }

  log(action: string, url?: string, details?: any): void {
    const log: ActivityLog = {
      id: this.generateId(),
      timestamp: new Date(),
      action,
      url,
      details,
      userAgent: navigator.userAgent,
      ip: undefined // Would be populated by server in real implementation
    };

    this.logs.unshift(log);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Store in localStorage for persistence
    this.saveToStorage();
    
    // In a real implementation, you'd also send this to a server
    this.sendToServer(log);
  }

  getLogs(limit?: number): ActivityLog[] {
    return limit ? this.logs.slice(0, limit) : [...this.logs];
  }

  getLogsByAction(action: string, limit?: number): ActivityLog[] {
    const filtered = this.logs.filter(log => log.action === action);
    return limit ? filtered.slice(0, limit) : filtered;
  }

  getLogsByUrl(url: string, limit?: number): ActivityLog[] {
    const filtered = this.logs.filter(log => log.url === url);
    return limit ? filtered.slice(0, limit) : filtered;
  }

  clearLogs(): void {
    this.logs = [];
    this.saveToStorage();
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  getStatistics(): {
    totalLogs: number;
    uniqueUrls: number;
    mostAccessedUrls: Array<{ url: string; count: number }>;
    actionCounts: Record<string, number>;
    recentActivity: ActivityLog[];
  } {
    const urlCounts = new Map<string, number>();
    const actionCounts: Record<string, number> = {};

    this.logs.forEach(log => {
      if (log.url) {
        urlCounts.set(log.url, (urlCounts.get(log.url) || 0) + 1);
      }
      actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
    });

    const mostAccessedUrls = Array.from(urlCounts.entries())
      .map(([url, count]) => ({ url, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalLogs: this.logs.length,
      uniqueUrls: urlCounts.size,
      mostAccessedUrls,
      actionCounts,
      recentActivity: this.logs.slice(0, 10)
    };
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem('json-dashboard-logs', JSON.stringify(this.logs));
    } catch (error) {
      console.warn('Failed to save logs to localStorage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('json-dashboard-logs');
      if (stored) {
        this.logs = JSON.parse(stored).map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp)
        }));
      }
    } catch (error) {
      console.warn('Failed to load logs from localStorage:', error);
    }
  }

  private async sendToServer(log: ActivityLog): Promise<void> {
    // In a real implementation, this would send the log to your server
    // For now, we'll just log it to the console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Activity Log:', log);
    }

    // Example server implementation:
    /*
    try {
      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(log),
      });
    } catch (error) {
      console.warn('Failed to send log to server:', error);
    }
    */
  }

  // Performance monitoring
  logPerformance(metric: string, value: number, unit: string = 'ms'): void {
    this.log('performance', undefined, {
      metric,
      value,
      unit,
      timestamp: Date.now()
    });
  }

  // Error tracking
  logError(error: Error, context?: any): void {
    this.log('error', undefined, {
      message: error.message,
      stack: error.stack,
      context
    });
  }

  // User interactions
  logInteraction(action: string, element?: string, details?: any): void {
    this.log('interaction', undefined, {
      action,
      element,
      details
    });
  }

  // Data operations
  logDataOperation(operation: string, url?: string, details?: any): void {
    this.log(`data_${operation}`, url, details);
  }

  constructor() {
    this.loadFromStorage();
  }
}

// Export singleton instance
export const activityLogger = ActivityLogger.getInstance();
